import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ConnectWallet,
  Web3Button,
  useContract,
  useAddress,
  useTokenBalance,
  darkTheme,
} from "@thirdweb-dev/react";
import { BigNumber, ethers } from "ethers";
import "../styles/Home.css"; // Assuming similar styling
import "../styles/commonButtonStyles.css"; // Import common button styles

// Constants - ideally these would be in a shared constants file
const AURA_TOKEN_ADDRESS = "0x3DB3aa87121d16407cFbbADf3f1c5b519182a399";
const HERESY_TOKEN_ADDRESS = "0x432d38f83a50ec77c409d086e97448794cf76dcf";
const TOKEN_SWAP_CONTRACT_ADDRESS = "0xDD7dc7a00edDe2c565F9Ea3F86b8f37a4dfefB03";
const NFT_STAKE_CONTRACT_ADDRESS = "0x51697170F78136c8d143B0013Cf5B229aDe70757"; // Staking contract for eligibility

const TOKEN_SWAP_ABI = [
  { "type": "function", "name": "tokenA", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "address" }] },
  { "type": "function", "name": "tokenB", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "address" }] },
  { "type": "function", "name": "rateNumeratorB", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "uint256" }] },
  { "type": "function", "name": "rateDenominatorA", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "uint256" }] },
  { "type": "function", "name": "maxWeeklySwapLimitTokenA", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "uint256" }] },
  { "type": "function", "name": "userWeeklySwappedAmount", "stateMutability": "view", "inputs": [{ "name": "userAddress", "type": "address" }, { "name": "weekId", "type": "uint256" }], "outputs": [{ "name": "", "type": "uint256" }] },
  { "type": "function", "name": "swap", "stateMutability": "nonpayable", "inputs": [{ "name": "_amountA", "type": "uint256" }], "outputs": [] },
  { "type": "event", "name": "TokensSwapped", "inputs": [ { "name": "user", "type": "address", "indexed": true }, { "name": "fromToken", "type": "address", "indexed": true }, { "name": "toToken", "type": "address", "indexed": true }, { "name": "amountFrom", "type": "uint256", "indexed": false }, { "name": "amountTo", "type": "uint256", "indexed": false } ], "anonymous": false }
];

const NFT_STAKE_ABI = [
    { "type": "function", "name": "getStakeInfo", "stateMutability": "view", "inputs": [{ "name": "_staker", "type": "address" }], "outputs": [{ "name": "_stakedTokenIds", "type": "uint256[]" }, { "name": "_totalRewards", "type": "uint256" }] }
];

// --- Replicated UI Components & Helpers (Ideally in shared files) ---
// const IconWrapper = ({ icon }: { icon: React.ReactNode }) => <>{icon}</>; // Not used here, can be removed if not needed elsewhere

const LoadingIndicator = ({isActive}: {isActive: boolean}): JSX.Element | null => {
  if (!isActive) return null;
  return (
    <div className="loading-overlay">
      <div className="loading-spinner"><div className="spinner-inner"></div></div>
      <div className="loading-text fb">Transaction in progress...</div>
    </div>
  );
};

const Notification = ({ message, type, isVisible, onClose }: { message: string; type: 'success' | 'error' | 'info'; isVisible: boolean; onClose: () => void; }): JSX.Element | null => {
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [isVisible, onClose]);
  if (!isVisible) return null;
  return (
    <div className={`notification ${type}-notification`}>
      <div className="notification-content">
        <div className={`notification-icon ${type}-icon`}></div>
        <div className="notification-message">{message}</div>
      </div>
      <button className="notification-close" onClick={onClose}>×</button>
    </div>
  );
};

// const Tooltip = ({text, children}: {text: string; children: React.ReactNode}): JSX.Element => ( // Not used here
//   <div className="tooltip-container">
//     {children}
//     <div className="tooltip-text">{text}</div>
//   </div>
// );

const formatLargeNumber = (num: number, fixedDecimals?: number): string => {
    const effectiveDecimals = fixedDecimals !== undefined ? fixedDecimals : (num < 1000 && !Number.isInteger(num) ? 2 : 0); 
    if (num >= 1000000000) return (num / 1000000000).toFixed(fixedDecimals ?? 2) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(fixedDecimals ?? 2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(fixedDecimals ?? 2) + 'K';
    return num.toFixed(effectiveDecimals);
};
// --- End Replicated UI Components & Helpers ---

const TokenSwap = (): JSX.Element => {
  const address = useAddress();
  const { contract: tokenSwapContract } = useContract(TOKEN_SWAP_CONTRACT_ADDRESS, TOKEN_SWAP_ABI);
  const { contract: heresyTokenContract } = useContract(HERESY_TOKEN_ADDRESS, "token");
  const { contract: auraTokenContract } = useContract(AURA_TOKEN_ADDRESS, "token");
  const { contract: nftStakeContract } = useContract(NFT_STAKE_CONTRACT_ADDRESS, NFT_STAKE_ABI);

  const { data: heresyBalance, refetch: refetchHeresyBalance } = useTokenBalance(heresyTokenContract, address);
  const { data: auraBalance, refetch: refetchAuraBalance } = useTokenBalance(auraTokenContract, address);

  const [heresyAmountToSwap, setHeresyAmountToSwap] = useState<string>("");
  const [auraToReceive, setAuraToReceive] = useState<string>("0");
  const [rateNumerator, setRateNumerator] = useState<BigNumber | null>(null);
  const [rateDenominator, setRateDenominator] = useState<BigNumber | null>(null);
  const [maxWeeklyLimit, setMaxWeeklyLimit] = useState<BigNumber | null>(null);
  const [swappedThisWeek, setSwappedThisWeek] = useState<BigNumber | null>(null);
  const [hasStakedNft, setHasStakedNft] = useState<boolean>(false);
  const [needsApproval, setNeedsApproval] = useState<boolean>(true);
  const [isCheckingStakedNft, setIsCheckingStakedNft] = useState<boolean>(true);
  
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotificationState] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean; }>({ message: '', type: 'info', isVisible: false });

  // Log state changes for heresyAmountToSwap
  useEffect(() => {
    console.log("[State Change] heresyAmountToSwap is now:", heresyAmountToSwap);
  }, [heresyAmountToSwap]);

  // Diagnostic log for button disabled states
  useEffect(() => {
    console.log("[State Check] hasStakedNft:", hasStakedNft, "isLoading:", isLoading);
  }, [hasStakedNft, isLoading]);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => setNotificationState({ message, type, isVisible: true });
  const hideNotification = () => setNotificationState(prev => ({ ...prev, isVisible: false }));

  const getRemainingWeeklyLimitBN = useCallback((): BigNumber | null => {
    if (maxWeeklyLimit && swappedThisWeek) {
      const remaining = maxWeeklyLimit.sub(swappedThisWeek);
      return remaining.lt(0) ? BigNumber.from(0) : remaining;
    }
    return null;
  }, [maxWeeklyLimit, swappedThisWeek]);

  const fetchContractData = useCallback(async () => {
    if (!tokenSwapContract || !address) return;
    try {
      const [num, den, limit] = await Promise.all([
        tokenSwapContract.call("rateNumeratorB"),
        tokenSwapContract.call("rateDenominatorA"),
        tokenSwapContract.call("maxWeeklySwapLimitTokenA")
      ]);
      setRateNumerator(num);
      setRateDenominator(den);
      setMaxWeeklyLimit(limit);
      const currentWeekId = BigNumber.from(Math.floor(Date.now() / 1000 / (7 * 24 * 60 * 60)));
      const swapped = await tokenSwapContract.call("userWeeklySwappedAmount", [address, currentWeekId]);
      setSwappedThisWeek(swapped);
    } catch (error) {
      console.error("Error fetching swap contract data:", error);
      showNotification("Error fetching swap details.", "error");
    }
  }, [tokenSwapContract, address]);

  const checkAllowance = useCallback(async () => {
    if (!heresyTokenContract || !address || !heresyBalance?.decimals) {
        setNeedsApproval(true); 
        return;
    }
    const amountStr = heresyAmountToSwap || "0";
    let amountToCheckBN: BigNumber;
    try {
      amountToCheckBN = ethers.utils.parseUnits(amountStr, heresyBalance.decimals);
    } catch (e) {
      if (amountStr === "" || amountStr === "." || parseFloat(amountStr) <= 0 || isNaN(parseFloat(amountStr))) {
        setNeedsApproval(false);
      } else {
        setNeedsApproval(true); 
      }
      return; 
    }
    if (amountToCheckBN.isNegative() || amountToCheckBN.isZero()) {
      setNeedsApproval(false);
      return;
    }
    try {
      const allowanceResult = await heresyTokenContract.allowance(TOKEN_SWAP_CONTRACT_ADDRESS);
      setNeedsApproval(allowanceResult.value.lt(amountToCheckBN));
    } catch (error) {
      console.error("Error fetching allowance:", error);
      setNeedsApproval(true); 
    }
  }, [heresyTokenContract, address, heresyAmountToSwap, heresyBalance?.decimals]);

  const checkStakedNFT = useCallback(async () => {
    if (!nftStakeContract || !address) return;
    setIsCheckingStakedNft(true);
    try {
      const stakeInfo = await nftStakeContract.call("getStakeInfo", [address]);
      setHasStakedNft(stakeInfo && stakeInfo[0] && stakeInfo[0].length > 0);
    } catch (error) {
      console.error("Error checking staked NFT:", error);
      setHasStakedNft(false);
    } finally {
      setIsCheckingStakedNft(false);
    }
  }, [nftStakeContract, address]);

  useEffect(() => {
    if (address && tokenSwapContract) {
      fetchContractData();
      checkStakedNFT();
    }
  }, [address, tokenSwapContract, fetchContractData, checkStakedNFT]);

  useEffect(() => {
    if (address && heresyTokenContract && heresyBalance?.decimals) {
        console.log("[Effect] Checking allowance due to change in: address, heresyTokenContract, heresyAmountToSwap, or heresyBalance.decimals. Current heresyAmountToSwap:", heresyAmountToSwap);
        checkAllowance();
    }
  }, [address, heresyTokenContract, heresyAmountToSwap, heresyBalance?.decimals, checkAllowance]);

  useEffect(() => {
    if (heresyAmountToSwap && rateNumerator && rateDenominator && rateDenominator.gt(0) && heresyBalance?.decimals && auraBalance?.decimals) {
      try {
        const numericValue = parseFloat(heresyAmountToSwap);
        if (isNaN(numericValue) || numericValue <= 0) {
            setAuraToReceive("0");
            return;
        }
        const amountInBN = ethers.utils.parseUnits(heresyAmountToSwap, heresyBalance.decimals);
        const amountOutBN = amountInBN.mul(rateNumerator).div(rateDenominator);
        setAuraToReceive(ethers.utils.formatUnits(amountOutBN, auraBalance.decimals));
      } catch (e) {
        setAuraToReceive("0"); 
      }
    } else {
      setAuraToReceive("0");
    }
  }, [heresyAmountToSwap, rateNumerator, rateDenominator, heresyBalance?.decimals, auraBalance?.decimals]);

  const handleHeresyAmountChange = (value: string) => {
    console.log("[Input Handler] handleHeresyAmountChange received value:", value);
    let sValue = value.replace(',', '.'); 
    const parts = sValue.split('.');
    if (parts.length > 1) {
        sValue = `${parts[0]}.${parts.slice(1).join('')}`;
        const subParts = sValue.split('.');
        if (subParts.length > 1) {
            sValue = `${subParts[0]}.${subParts[1].replace(/[^0-9]/g, '')}`;
        }
    }
    if (parts.length === 1) {
        sValue = sValue.replace(/[^0-9]/g, '');
    }
    if (sValue !== "" && !/^(\d*\.?\d*|\.\d+)$/.test(sValue)) {
       const match = sValue.match(/^(\d*\.?\d*)/);
       sValue = match ? match[0] : "";
    }
    if (sValue.startsWith("0") && sValue.length > 1 && sValue[1] !== '.') {
        sValue = "0"; 
    }
    if (sValue.startsWith("..") || (sValue.startsWith(".") && sValue.length > 1 && isNaN(parseInt(sValue[1])))) {
        sValue = "0"; 
    }

    let finalValueToSet = sValue;
    console.log("[Input Handler] Sanitized sValue:", sValue);

    if (heresyBalance?.decimals && sValue) {
        const numericValue = parseFloat(sValue);
        if (!isNaN(numericValue) && numericValue > 0) {
            try {
                const valueBN = ethers.utils.parseUnits(sValue, heresyBalance.decimals);
                const remainingLimitBN = getRemainingWeeklyLimitBN();
                console.log("[Input Handler] Parsed valueBN:", valueBN.toString(), "Remaining limitBN:", remainingLimitBN?.toString());

                if (remainingLimitBN !== null && valueBN.gt(remainingLimitBN)) {
                    if (remainingLimitBN.isZero()) {
                        finalValueToSet = "0";
                    } else {
                        finalValueToSet = ethers.utils.formatUnits(remainingLimitBN, heresyBalance.decimals);
                    }
                    console.log("[Input Handler] Amount capped. finalValueToSet:", finalValueToSet);
                    if (finalValueToSet !== sValue) { 
                        showNotification("Amount capped at remaining weekly limit.", "info");
                    }
                }
            } catch (e) {
                 console.warn("[Input Handler] Error parsing sValue for capping or during capping logic:", sValue, e);
            }
        }
    }
    console.log("[Input Handler] Attempting to setHeresyAmountToSwap with:", finalValueToSet);
    setHeresyAmountToSwap(finalValueToSet);
  };

  const handleSetFixedAmount = (amount: string) => {
    console.log("[Button Action] handleSetFixedAmount called with:", amount);
    handleHeresyAmountChange(amount);
  };

  const handleSetMax = () => {
    console.log("[Button Action] handleSetMax called");
    if (!heresyBalance?.value || !heresyBalance?.decimals) {
        console.warn("[Button Action] handleSetMax: Heresy balance or decimals not available.");
        return;
    }
    const remainingLimitBN = getRemainingWeeklyLimitBN();
    let maxCanSwap: BigNumber;
    if (remainingLimitBN !== null) {
      const userBalanceBN = heresyBalance.value;
      maxCanSwap = remainingLimitBN.lt(userBalanceBN) ? remainingLimitBN : userBalanceBN;
    } else { 
      maxCanSwap = heresyBalance.value;
    }

    if (maxCanSwap.lt(BigNumber.from(0))) { // Ensure it's not negative
        maxCanSwap = BigNumber.from(0);
    }

    let maxAmountString: string;
    if (maxCanSwap.isZero()) {
        maxAmountString = "0";
    } else {
        maxAmountString = ethers.utils.formatUnits(maxCanSwap, heresyBalance.decimals);
    }
    
    console.log("[Button Action] handleSetMax: Calculated max amount string:", maxAmountString, "(Original from formatUnits if not zero:", ethers.utils.formatUnits(maxCanSwap, heresyBalance.decimals), ")");
    handleHeresyAmountChange(maxAmountString);
  };

  const handleApprove = async () => {
    if (!heresyTokenContract || !heresyAmountToSwap || !heresyBalance?.decimals || parseFloat(heresyAmountToSwap) <= 0) {
      showNotification("Approval pre-check failed. Enter a valid amount.", "error");
      return;
    }
    setIsLoading(true);
    showNotification("Approving $HERESY...", "info");
    try {
      const amountToApprove = ethers.utils.parseUnits(heresyAmountToSwap, heresyBalance.decimals);
      await heresyTokenContract.setAllowance(TOKEN_SWAP_CONTRACT_ADDRESS, amountToApprove.toString());
      showNotification("$HERESY Approved! You can now swap.", "success");
      await checkAllowance(); 
    } catch (error: any) {
      console.error("Approval failed:", error);
      showNotification(`Approval failed: ${error.message || "Unknown error"}`, "error");
    }
    setIsLoading(false);
  };

  const handleSwap = async () => {
    if (!tokenSwapContract || !heresyAmountToSwap || !heresyBalance?.decimals || !address || !hasStakedNft || needsApproval || !rateNumerator || !rateDenominator || parseFloat(heresyAmountToSwap) <= 0) {
      if (!hasStakedNft) showNotification("You must have at least one NFT staked to swap.", "error");
      else if (needsApproval) showNotification("Please approve $HERESY tokens first.", "error");
      else if (parseFloat(heresyAmountToSwap) <= 0) showNotification("Please enter a valid amount to swap.", "error");
      else showNotification("Swap pre-check failed. Ensure all conditions are met.", "error");
      return;
    }
    setIsLoading(true);
    showNotification("Processing swap...", "info");
    try {
      const amountInBN = ethers.utils.parseUnits(heresyAmountToSwap, heresyBalance.decimals);
      if (maxWeeklyLimit && swappedThisWeek && swappedThisWeek.add(amountInBN).gt(maxWeeklyLimit)) {
        showNotification("Swap amount exceeds weekly limit.", "error");
        setIsLoading(false); return;
      }
      if (heresyBalance?.value && amountInBN.gt(heresyBalance.value)) {
        showNotification("Insufficient $HERESY balance.", "error");
        setIsLoading(false); return;
      }
      await tokenSwapContract.call("swap", [amountInBN]);
      showNotification("Swap successful!", "success");
      fetchContractData(); 
      if (refetchHeresyBalance) refetchHeresyBalance();
      if (refetchAuraBalance) refetchAuraBalance();
      setHeresyAmountToSwap(""); 
    } catch (error: any) {
      console.error("Swap failed:", error);
      const message = error.reason || error.message || "Unknown error during swap.";
      showNotification(`Swap failed: ${message}`, "error");
    }
    setIsLoading(false);
  };
  
  const displayableRemainingWeeklyLimit = () => {
    if (maxWeeklyLimit && swappedThisWeek && heresyBalance?.decimals) {
      const remainingBN = getRemainingWeeklyLimitBN();
      if (remainingBN === null) return "Loading...";
      const numDecimals = heresyBalance.decimals > 3 ? 2 : heresyBalance.decimals;
      return formatLargeNumber(Number(ethers.utils.formatUnits(remainingBN, heresyBalance.decimals)), numDecimals);
    }
    return "Loading...";
  };

  const quickAmountButtonStyle: React.CSSProperties = {
    padding: '10px 15px',
    fontSize: '1em',
    backgroundColor: '#0c0c1f',
    border: '1px solid #00ffff',
    borderRadius: '5px',
    color: 'white',
    cursor: 'pointer',
    flexGrow: 1, 
    textAlign: 'center'
  };

  const canApproveOrSwap = hasStakedNft && !isLoading && parseFloat(heresyAmountToSwap || "0") > 0;
  const heresyDisplayDecimals = heresyBalance?.decimals ? (heresyBalance.decimals > 3 ? 2 : heresyBalance.decimals) : 2;
  const auraDisplayDecimals = auraBalance?.decimals ? (auraBalance.decimals > 3 ? 2 : auraBalance.decimals) : 2;

  return (
    <>
      <LoadingIndicator isActive={isLoading} />
      <Notification message={notification.message} type={notification.type} isVisible={notification.isVisible} onClose={hideNotification} />
      
      <div className="main bg1" style={{ minHeight: '10vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px 0' }}>
        <h1 className="fa head" style={{ margin: 0, fontSize: '48px', textAlign: 'center' }}>
          Swap HERESY for AURA<br></br>
        </h1>
        
        <Link to="/" className="back-link">
          <div className="back-button">
            <span className="fa">Back to The City</span>
          </div>
        </Link>
      </div>
      <div className="section-divider-animated"></div>

      <div className="bg-secondary" style={{ padding: '20px 0' }}> 
        <div className="colbox bg3 botmar" style={{ maxWidth: '600px', margin: '0 auto', padding: '30px', borderRadius: '10px' }}> 
          {!address ? (
            <ConnectWallet className="ccbutton" style={{ fontFamily: 'Doctor Glitch', fontWeight: 'normal', fontStyle: 'oblique', color: 'white', margin: '20px auto' }} theme={darkTheme({ fontFamily: "Hacked, sans-serif", colors: { modalBg: "#002020", accentText: "cyan" } })} />
          ) : (
            <>
              {!isCheckingStakedNft && !hasStakedNft && (
                <div className="error-message fb" style={{ textAlign: 'center', marginBottom: '20px', padding: '10px', border: '1px solid red', borderRadius: '5px' }}>
                  You must have at least one Analog Distortion NFT staked to use the swap. Please stake an NFT on the <Link to="/blubstaking" style={{color: '#00ffff'}}>staking page</Link>.
                </div>
              )}

              <div className="balance-row padtop padbot" style={{ justifyContent: 'space-around', marginBottom: '30px' }}> 
                <div className="balance-item fb">
                  <b>{heresyBalance ? formatLargeNumber(Number(heresyBalance.displayValue), heresyDisplayDecimals) : "0.00"}</b>
                  <b>Your $HERESY</b>
                </div>
                <div className="balance-item fb">
                  <b>{auraBalance ? formatLargeNumber(Number(auraBalance.displayValue), auraDisplayDecimals) : "0.00"}</b>
                  <b>Your $AURA</b>
                </div>
              </div>

              {rateNumerator && rateDenominator && rateDenominator.gt(0) && heresyBalance?.decimals && auraBalance?.decimals && (
                <p className="fb" style={{textAlign: 'center', marginBottom: '20px', fontSize: '1.1em'}}>
                  Swap Rate<br/> {ethers.utils.formatUnits(rateDenominator, heresyBalance.decimals)} $HERESY - {ethers.utils.formatUnits(rateNumerator, auraBalance.decimals)} $AURA
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '25px' }}>
                <label htmlFor="heresyAmount" className="fb" style={{ display: 'block', marginBottom: '10px', fontSize: '1.2em', fontWeight: 'bold' }}>Amount of $HERESY to swap:</label>
                <input
                    type="text" 
                    inputMode="decimal" 
                    id="heresyAmount"
                    value={heresyAmountToSwap}
                    onChange={(e) => handleHeresyAmountChange(e.target.value)}
                    placeholder="0.0"
                    className="input-field fb" 
                    style={{
                      width: '90%', 
                      padding: '15px',
                      backgroundColor: '#0c0c1f', 
                      border: '1px solid #00ffff',
                      borderRadius: '5px',
                      color: 'white',
                      fontSize: '1.3em',
                      fontFamily: '"Doctor Glitch", monospace',
                      WebkitAppearance: 'none', 
                      MozAppearance: 'textfield',
                      textAlign: 'center', 
                      marginBottom: '15px' 
                    }}
                    disabled={isLoading}
                  />

                {/* START DIAGNOSTIC LOG */}
                {(() => {
                  const areQuickButtonsDisabled = !hasStakedNft || isLoading;
                  console.log(
                    "[Render Check] QuickButtons. Intended disabled state:", areQuickButtonsDisabled,
                    "| hasStakedNft:", hasStakedNft,
                    "| isLoading:", isLoading,
                    "| notification.isVisible:", notification.isVisible
                  );
                  return null; // This IIFE is just for logging and should not render anything
                })()}
                {/* END DIAGNOSTIC LOG */}

                <div className="quick-amounts-buttons-row" style={{display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '15px', width: '90%', position: 'relative', zIndex: 5}}>
                    <button
                        style={quickAmountButtonStyle}
                        onClick={() => { console.log("[Button Click] 0.01 button pressed"); handleSetFixedAmount("0.01"); }}
                        disabled={isLoading}
                    >0.01</button>
                    <button
                        style={quickAmountButtonStyle}
                        onClick={() => { console.log("[Button Click] 0.025 button pressed"); handleSetFixedAmount("0.025"); }}
                        disabled={isLoading}
                    >0.025</button>
                    <button
                      onClick={() => { console.log("[Button Click] Max button pressed"); handleSetMax(); }}
                      style={{...quickAmountButtonStyle}}
                      disabled={isLoading || getRemainingWeeklyLimitBN() === null || !heresyBalance?.value || !heresyBalance?.decimals || (getRemainingWeeklyLimitBN()?.isZero() && heresyBalance.value.isZero())}
                    >
                     Max
                    </button>
                </div>

                <p className="fb" style={{fontSize: '1.1em', marginTop: '10px', textAlign: 'center'}}>You will receive<br/><b style={{color: '#00ffff', fontSize: '1.2em'}}>{formatLargeNumber(Number(auraToReceive), auraDisplayDecimals)} $AURA</b></p>
              </div>
              
              <div className="swap-limits-info fb" style={{ marginBottom: '30px', textAlign: 'center', fontSize: '1.1em' }}> 
                 <p style={{marginBottom: '5px'}}>Maximum Weekly Swap: {maxWeeklyLimit && heresyBalance?.decimals ? `${formatLargeNumber(Number(ethers.utils.formatUnits(maxWeeklyLimit, heresyBalance.decimals)), heresyDisplayDecimals)} $HERESY` : 'Loading...'}</p>
                 <p style={{marginBottom: '5px'}}>Swapped This Week: {swappedThisWeek && heresyBalance?.decimals ? `${formatLargeNumber(Number(ethers.utils.formatUnits(swappedThisWeek, heresyBalance.decimals)), heresyDisplayDecimals)} $HERESY` : 'Loading...'}</p>
                 <p>Remaining This Week: {heresyBalance?.decimals ? `${displayableRemainingWeeklyLimit()} $HERESY` : 'Loading...'}</p>
              </div>

              {needsApproval ? (
                <Web3Button
                  contractAddress={HERESY_TOKEN_ADDRESS}
                  action={handleApprove}
                  className="ccbutton approve-mode" 
                  isDisabled={!canApproveOrSwap}
                  style={{width: '100%', padding: '15px 0'}}
                >
                  <h3 className="fb" style={{fontSize: '1.4em'}}>Approve $HERESY</h3>
                </Web3Button>
              ) : (
                <Web3Button
                  contractAddress={TOKEN_SWAP_CONTRACT_ADDRESS}
                  action={handleSwap}
                  className="ccbutton explore-mode" 
                  isDisabled={!canApproveOrSwap || needsApproval}
                  style={{width: '100%', padding: '15px 0'}}
                >
                  <h3 className="fb" style={{fontSize: '1.4em'}}>Swap for $AURA</h3>
                </Web3Button>
              )}
            </>
          )}
        </div>
        <span className="developer-credit fb" style={{ marginTop: '30px', display: 'block', textAlign: 'center' }}>Developed by <a href="https://x.com/SnapsNoCaps" target="_blank" rel="noopener noreferrer">snaps</a> with 🩵</span>
      </div>
    </>
  );
};

export default TokenSwap; 